/**
 * Utilidades puras del origen de noticias externo (feature #118).
 *
 * Este módulo lo consumen el admin (validación/normalización de la URL) y las
 * Cloud Functions (construcción de las peticiones al WordPress del tenant), por
 * eso NO tiene dependencias: ni Angular, ni `firebase-admin`, ni `@models` en
 * runtime. Todo lo que hay aquí es determinista y testeable en aislamiento.
 */

// ── Constantes de la ventana de sincronización (design.md §2.3) ──────────────

/** Solape de seguridad restado a `lastSyncAt` al construir `modified_after` (R22). */
export const WP_SYNC_OVERLAP_MINUTES = 5;

/** Posts pedidos en la primera sincronización de un tenant (una sola página, R21). */
export const WP_FIRST_SYNC_PER_PAGE = 20;

/** Tamaño de página de las sincronizaciones incrementales. */
export const WP_INCREMENTAL_PER_PAGE = 50;

/** Tope de páginas de posts por ejecución (5 × 50 = 250 posts). */
export const WP_MAX_POST_PAGES = 5;

/** Tamaño de página del barrido de ids publicados (`_fields=id`). */
export const WP_IDS_PER_PAGE = 100;

/** Tope de páginas del barrido de ids (10 × 100 = 1.000 ids). */
export const WP_MAX_ID_PAGES = 10;

/** Ruta del endpoint REST de posts de WordPress. */
const WP_POSTS_PATH = '/wp-json/wp/v2/posts';

// ── URL base del sitio ───────────────────────────────────────────────────────

/**
 * Normaliza la URL raíz del WordPress de un tenant (R8):
 * - recorta espacios,
 * - fuerza el esquema `https`,
 * - elimina query string y fragmento,
 * - elimina la barra final,
 * - elimina los sufijos `/wp-json`, `/wp-json/wp/v2` y `/wp-json/wp/v2/posts`
 *   si el admin pegó el endpoint REST en vez de la raíz del sitio.
 *
 * Devuelve `''` si la entrada no es una URL absoluta `http`/`https`: la función
 * NO inventa el esquema, de modo que validar y normalizar en cualquier orden dé
 * el mismo veredicto (R9).
 */
export function normalizeWordPressBaseUrl(input: string | null | undefined): string {
  const raw = (input ?? '').trim();
  if (!raw) return '';
  if (!/^https?:\/\//i.test(raw)) return '';

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return '';
  }
  if (!url.hostname) return '';

  url.protocol = 'https:';
  url.search = '';
  url.hash = '';

  const path = url.pathname
    .replace(/\/+$/, '')
    .replace(/\/wp-json(\/wp\/v2(\/posts)?)?$/i, '')
    .replace(/\/+$/, '');

  return `${url.origin}${path}`;
}

/**
 * `true` si la entrada es una URL absoluta con esquema `http`/`https` y un host
 * con punto (descarta `localhost`, hosts de una sola etiqueta y rutas
 * relativas). Se evalúa sobre la entrada **cruda** del admin (R9).
 */
export function isValidWordPressBaseUrl(input: string | null | undefined): boolean {
  const raw = (input ?? '').trim();
  if (!raw || !/^https?:\/\//i.test(raw)) return false;

  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    const host = url.hostname;
    if (!host.includes('.')) return false;
    // Un host no puede empezar ni acabar por punto ni tener puntos seguidos.
    if (/^\.|\.$|\.\./.test(host)) return false;
    return true;
  } catch {
    return false;
  }
}

// ── Identificador determinista del documento ─────────────────────────────────

/**
 * Id de documento determinista de una noticia importada de WordPress (R25):
 * `wp_<postId>`. Dos ejecuciones sobre el mismo post escriben el mismo doc, así
 * que la ingesta nunca duplica.
 */
export function wordPressNewsDocId(postId: number | string): string {
  return `wp_${String(postId).trim()}`;
}

// ── Construcción de las peticiones REST ──────────────────────────────────────

export interface WpPostsQuery {
  /** Página 1-based. Por defecto `1`. */
  page?: number;
  /**
   * Fecha del último sync correcto del tenant. Ausente/`null` ⇒ primera
   * sincronización: una sola página de los posts más recientes, sin
   * `modified_after` (R21).
   */
  lastSyncAt?: Date | null;
  /** Filtro por categorías de WordPress (R23). */
  categoryIds?: number[];
  /** Idioma para WordPress multi-idioma (Polylang/WPML) (R24). */
  language?: string;
  /** Sobrescribe el tamaño de página derivado de `lastSyncAt`. */
  perPage?: number;
}

export interface WpPostIdsQuery {
  /** Página 1-based. Por defecto `1`. */
  page?: number;
  categoryIds?: number[];
  language?: string;
}

/**
 * URL de la petición de posts al WordPress del tenant.
 *
 * - **Primer sync** (`lastSyncAt` ausente): `per_page=20&orderby=date&order=desc`
 *   y **sin** `modified_after` — solo las noticias actuales (R21, design §2.3).
 * - **Sync incremental**: `modified_after = lastSyncAt - 5 min` (R22),
 *   `orderby=modified&order=asc`, `per_page=50`.
 * - `categories` y `lang` se añaden solo si vienen informados (R23, R24).
 *
 * Se pide `_embed=wp:featuredmedia` para poder resolver la portada sin una
 * segunda petición a `/media`.
 */
export function buildWpPostsUrl(baseUrl: string, opts: WpPostsQuery = {}): string {
  const root = normalizeWordPressBaseUrl(baseUrl);
  const incremental = !!opts.lastSyncAt;
  const perPage = opts.perPage ?? (incremental ? WP_INCREMENTAL_PER_PAGE : WP_FIRST_SYNC_PER_PAGE);

  const params: [string, string][] = [
    ['per_page', String(perPage)],
    ['page', String(Math.max(1, opts.page ?? 1))],
    ['status', 'publish'],
    ['orderby', incremental ? 'modified' : 'date'],
    ['order', incremental ? 'asc' : 'desc'],
    ['_embed', 'wp:featuredmedia'],
  ];

  if (opts.lastSyncAt) {
    params.push(['modified_after', wpModifiedAfter(opts.lastSyncAt)]);
  }
  appendFilters(params, opts.categoryIds, opts.language);

  return `${root}${WP_POSTS_PATH}?${toQueryString(params)}`;
}

/**
 * URL del barrido de ids publicados (detección de bajas, design §2.3):
 * `_fields=id&per_page=100`, ordenado de forma estable por id.
 */
export function buildWpPostIdsUrl(baseUrl: string, opts: WpPostIdsQuery = {}): string {
  const root = normalizeWordPressBaseUrl(baseUrl);

  const params: [string, string][] = [
    ['_fields', 'id'],
    ['per_page', String(WP_IDS_PER_PAGE)],
    ['page', String(Math.max(1, opts.page ?? 1))],
    ['status', 'publish'],
    ['orderby', 'id'],
    ['order', 'asc'],
  ];
  appendFilters(params, opts.categoryIds, opts.language);

  return `${root}${WP_POSTS_PATH}?${toQueryString(params)}`;
}

/**
 * Instante que se envía en `modified_after`: `lastSyncAt` menos el solape de
 * seguridad de 5 minutos (R22), en ISO-8601 UTC.
 *
 * Nota: WordPress interpreta `modified_after` en la zona horaria del sitio. Con
 * tenants en `Europe/Madrid` (UTC+1/+2) un instante UTC se lee como una hora
 * *anterior* a la real, así que la ventana solo puede ensancharse — nunca
 * perder posts. Reprocesar es inocuo: el doc id `wp_<id>` hace la escritura
 * idempotente.
 */
export function wpModifiedAfter(lastSyncAt: Date, overlapMinutes = WP_SYNC_OVERLAP_MINUTES): string {
  const from = new Date(lastSyncAt.getTime() - overlapMinutes * 60_000);
  return from.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function appendFilters(params: [string, string][], categoryIds?: number[], language?: string): void {
  const categories = (categoryIds ?? []).filter((id) => Number.isFinite(id));
  if (categories.length) params.push(['categories', categories.join(',')]);

  const lang = (language ?? '').trim();
  if (lang) params.push(['lang', lang]);
}

function toQueryString(params: [string, string][]): string {
  return params.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');
}

// ── Texto ────────────────────────────────────────────────────────────────────

/** Entidades con nombre más habituales en los títulos de WordPress. */
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  hellip: '…',
  ndash: '–',
  mdash: '—',
  lsquo: '‘',
  rsquo: '’',
  sbquo: '‚',
  ldquo: '“',
  rdquo: '”',
  bdquo: '„',
  laquo: '«',
  raquo: '»',
  bull: '•',
  middot: '·',
  deg: '°',
  euro: '€',
  copy: '©',
  reg: '®',
  trade: '™',
  aacute: 'á',
  eacute: 'é',
  iacute: 'í',
  oacute: 'ó',
  uacute: 'ú',
  ntilde: 'ñ',
  uuml: 'ü',
  Aacute: 'Á',
  Eacute: 'É',
  Iacute: 'Í',
  Oacute: 'Ó',
  Uacute: 'Ú',
  Ntilde: 'Ñ',
  Uuml: 'Ü',
  ccedil: 'ç',
  Ccedil: 'Ç',
  agrave: 'à',
  egrave: 'è',
  ograve: 'ò',
  Agrave: 'À',
  Egrave: 'È',
  Ograve: 'Ò',
  iquest: '¿',
  iexcl: '¡',
};

/**
 * Decodifica las entidades HTML de un texto (R41): numéricas decimales
 * (`&#231;`), numéricas hexadecimales (`&#x2019;`) y las entidades con nombre
 * habituales. Se resuelve en **una sola pasada**, de modo que `&amp;#039;`
 * queda en `&#039;` y no se decodifica dos veces.
 */
export function decodeHtmlEntities(input: string | null | undefined): string {
  if (!input) return '';

  return input.replace(/&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,31});/g, (match, entity: string) => {
    if (entity.startsWith('#')) {
      const isHex = entity[1] === 'x' || entity[1] === 'X';
      const code = Number.parseInt(isHex ? entity.slice(2) : entity.slice(1), isHex ? 16 : 10);
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return match;
      try {
        return String.fromCodePoint(code);
      } catch {
        return match;
      }
    }
    const named = NAMED_ENTITIES[entity];
    return named === undefined ? match : named;
  });
}

/**
 * Convierte HTML en texto plano legible: elimina los bloques `script`/`style`,
 * convierte los saltos de bloque en espacios, quita el resto de etiquetas,
 * decodifica entidades y colapsa los espacios. Con `maxLength` recorta por
 * palabra y añade `…`.
 *
 * Se usa para prellenar el cuerpo de una notificación push a partir del
 * contenido de una noticia.
 */
export function htmlToPlainText(html: string | null | undefined, maxLength?: number): string {
  if (!html) return '';

  const text = decodeHtmlEntities(
    html
      .replace(/<(script|style|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)\s*>/gi, ' ')
      .replace(/<[^>]*>/g, ''),
  )
    .replace(/\s+/g, ' ')
    .trim();

  if (!maxLength || maxLength <= 0 || text.length <= maxLength) return text;

  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  // Se corta por palabra; si la primera palabra ya excede el límite, corte duro.
  const truncated = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  return `${truncated.replace(/[\s.,;:]+$/, '')}…`;
}
